<template>
  <div class="form-wrapper">
    <section class="settings-section">
      <div class="section-heading-skeleton-block mb-4">
        <v-skeleton-loader
          type="heading"
          class="section-heading-skeleton section-heading-profile"
        />
      </div>
      <div class="description-skeleton description-skeleton-profile-fields mb-4">
        <v-skeleton-loader
          type="text"
          class="description-line-skeleton description-line-full"
        />
        <v-skeleton-loader
          type="text"
          class="description-line-skeleton description-line-short"
        />
      </div>

      <div class="column-header field-columns">
        <v-skeleton-loader
          type="text"
          class="field-col-field col-header-skeleton col-header-field"
        />
        <v-skeleton-loader
          type="text"
          class="field-col-visibility col-header-skeleton col-header-visibility"
        />
        <v-skeleton-loader
          type="text"
          class="field-col-status col-header-skeleton col-header-status"
        />
      </div>

      <div class="draggable-list">
        <div
          v-for="n in 8"
          :key="`field-${n}`"
          class="field-row"
        >
          <div class="field-col-field field-name-cell">
            <v-skeleton-loader
              type="text"
              class="drag-handle-skeleton"
            />
            <v-skeleton-loader
              type="text"
              class="row-number-skeleton"
            />
            <v-skeleton-loader
              type="text"
              class="field-name-skeleton"
            />
          </div>
          <div class="field-col-visibility">
            <v-skeleton-loader
              type="text"
              class="toggle-skeleton"
            />
          </div>
          <div class="field-col-status">
            <v-skeleton-loader
              type="text"
              class="toggle-skeleton toggle-skeleton-wide"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="settings-section">
      <div class="section-heading-skeleton-block mb-4">
        <v-skeleton-loader
          type="heading"
          class="section-heading-skeleton section-heading-levels"
        />
      </div>
      <div class="description-skeleton description-skeleton-levels mb-4">
        <v-skeleton-loader
          type="text"
          class="description-line-skeleton description-line-full"
        />
        <v-skeleton-loader
          type="text"
          class="description-line-skeleton description-line-medium"
        />
      </div>

      <div class="column-header level-columns">
        <v-skeleton-loader
          type="text"
          class="level-col-level col-header-skeleton col-header-level"
        />
        <v-skeleton-loader
          type="text"
          class="level-col-shortened col-header-skeleton col-header-shortened"
        />
      </div>

      <div class="draggable-list">
        <div
          v-for="n in 4"
          :key="`level-${n}`"
          class="level-row"
        >
          <div class="level-col-level level-name-cell">
            <v-skeleton-loader
              type="text"
              class="drag-handle-skeleton"
            />
            <v-skeleton-loader
              type="text"
              class="row-number-skeleton"
            />
            <v-skeleton-loader
              type="text"
              class="level-name-skeleton"
            />
          </div>
          <div class="level-col-shortened">
            <v-skeleton-loader
              type="text"
              class="alias-field-skeleton"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="less">
.form-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  border-radius: 10px;
  background: @white;
  padding: 40px;
}

.settings-section {
  width: 100%;
}

.section-heading-skeleton-block {
  width: 100%;
  max-width: 280px;
}

.description-skeleton-profile-fields,
.description-skeleton-levels {
  max-width: 560px;
}

.description-skeleton {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 560px;
}

.column-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  width: 100%;
  margin-bottom: 8px;
}

.field-columns {
  .field-col-field {
    flex: 0 0 300px;
    width: 300px;
  }

  .field-col-visibility {
    flex: 0 0 100px;
    width: 100px;
  }

  .field-col-status {
    flex: 0 0 168px;
    width: 168px;
    min-width: 168px;
  }
}

.level-columns {
  .level-col-level {
    flex: 0 0 400px;
    width: 400px;
  }

  .level-col-shortened {
    flex: 0 0 100px;
    width: 100px;
  }
}

.draggable-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.field-row,
.level-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
}

.field-row {
  margin-bottom: 16px;

  .field-col-field {
    flex: 0 0 300px;
    width: 300px;
  }

  .field-col-visibility {
    flex: 0 0 100px;
    width: 100px;
  }

  .field-col-status {
    flex: 0 0 168px;
    width: 168px;
    min-width: 168px;
  }
}

.level-row {
  .level-col-level {
    flex: 0 0 400px;
    width: 400px;
  }

  .level-col-shortened {
    flex: 0 0 100px;
    width: 100px;
  }
}

.field-name-cell,
.level-name-cell {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.section-heading-skeleton {
  :deep(.v-skeleton-loader) {
    background: transparent;
  }

  :deep(.v-skeleton-loader__heading) {
    margin: 0;
    border-radius: 6px;
  }
}

.section-heading-profile {
  width: 100%;

  :deep(.v-skeleton-loader) {
    width: 100%;
    max-width: 100%;
  }

  :deep(.v-skeleton-loader__heading) {
    width: 100%;
    height: 28px;
    max-width: 100%;
  }
}

.section-heading-levels {
  width: 100%;

  :deep(.v-skeleton-loader) {
    width: 100%;
    max-width: 100%;
  }

  :deep(.v-skeleton-loader__heading) {
    width: 100%;
    height: 28px;
    max-width: 100%;
  }
}

.description-line-skeleton {
  :deep(.v-skeleton-loader) {
    background: transparent;
  }

  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    margin: 0;
    height: 16px;
    border-radius: 6px;
  }
}

.description-line-full {
  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 100%;
    max-width: 100%;
  }
}

.description-line-short {
  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 90%;
    max-width: 90%;
  }
}

.description-line-medium {
  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 75%;
    max-width: 75%;
  }
}

.col-header-skeleton {
  :deep(.v-skeleton-loader) {
    background: transparent;
  }

  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    margin: 0;
    height: 14px;
    border-radius: 4px;
  }
}

.col-header-field {
  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 56px;
  }
}

.col-header-visibility {
  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 88px;
  }
}

.col-header-status {
  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 52px;
  }
}

.col-header-level {
  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 56px;
  }
}

.col-header-shortened {
  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 96px;
  }
}

.drag-handle-skeleton {
  flex-shrink: 0;

  :deep(.v-skeleton-loader) {
    background: transparent;
  }

  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
    margin: 0;
    border-radius: 4px;
  }
}

.row-number-skeleton {
  flex-shrink: 0;

  :deep(.v-skeleton-loader) {
    background: transparent;
  }

  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 16px;
    height: 16px;
    min-width: 16px;
    min-height: 16px;
    margin: 0;
    border-radius: 4px;
  }
}

.field-name-skeleton {
  flex: 1;
  min-width: 0;

  :deep(.v-skeleton-loader) {
    background: transparent;
    width: 100%;
  }

  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 100%;
    max-width: 220px;
    height: 18px;
    margin: 0;
    border-radius: 6px;
  }
}

.level-name-skeleton {
  flex-shrink: 0;

  :deep(.v-skeleton-loader) {
    background: transparent;
  }

  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 40px;
    height: 18px;
    margin: 0;
    border-radius: 6px;
  }
}

.toggle-skeleton {
  :deep(.v-skeleton-loader) {
    background: transparent;
  }

  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 72px;
    height: 32px;
    min-height: 32px;
    margin: 0;
    border-radius: 16px;
  }
}

.toggle-skeleton-wide {
  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 120px;
  }
}

.alias-field-skeleton {
  width: 100%;

  :deep(.v-skeleton-loader) {
    background: transparent;
  }

  :deep(.v-skeleton-loader__bone),
  :deep(.v-skeleton-loader__text) {
    width: 100px;
    height: 40px;
    min-height: 40px;
    margin: 0;
    border-radius: 8px;
  }
}
</style>
